'use strict';

import React from "react";
import { action, flow, observable, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {Breakpoints, DaysOfWeek, States} from '@uw-it-sis/lib-react/lib/AppConstants';
import appStore from "@uw-it-sis/lib-react/lib/AppStore";
import events from "@uw-it-sis/lib-react/lib/Events";
import {getBreakpointSize} from "@uw-it-sis/lib-react/lib/Utils";

import {normalize} from "@uw-it-sis/lib-react-myplan/lib/CourseUtils";

import scheduleBuilderStore from './ScheduleBuilderStore';
import reservedTimeFilterStore from "./ReservedTimeFilterStore";
import scheduleBuilderPlanStore from "./ScheduleBuilderPlanStore";
import {ScheduleBuilderEvents} from "./ScheduleBuilderConstants";
import scheduleBuilderDetailsStore from './ScheduleBuilderDetailsStore';

import api from "../lib/Api";

class ScheduleBuilderOptionsStore {
    state = States.initial;
    showDisplayLimitMessage = false;

    constructor() {
        makeObservable(this, {
            state: observable,
            showDisplayLimitMessage: observable,
            resetDisplayData: action,
            mount: action,
            toggleDisplayedSchedule: action,
            closeDisplayLimitMessage: action,
            openDisplayLimitMessage: action
        });

        this.componentKey = "ScheduleBuilderOptions";

        this.schedules = null;

        this.displayedEvents = [];
        this.scheduleDisplayClasses = [];
        this.displayedEventsLimit = 0;
        this.eventsLimitIndicator = "";

        // List of planned coursed and activities
        this.planData = [];

        this.termId = null;
        this.analyticsStore = appStore.getAnalyticsStore();
        this.exceptionUtils = appStore.getExceptionUtils();
    }

    resetDisplayData() {
        switch (getBreakpointSize()) {
            case Breakpoints.xs.code:
            case Breakpoints.sm.code:
                this.displayedEventsLimit = 2;
                this.eventsLimitIndicator = 2;
                break;
            case Breakpoints.md.code:
            case Breakpoints.lg.code:
            case Breakpoints.xl.code:
            default:
                this.displayedEventsLimit = 3;
                this.eventsLimitIndicator = 3
                break;
        }

        this.planData = [];
        this.scheduleDisplayClasses = ["scheduleBuilder-option--teal", "scheduleBuilder-option--blue", "scheduleBuilder-option--magenta"];
        this.showDisplayLimitMessage = false;
    }

    mount(termId) {
        this.termId = termId;
        this.resetDisplayData();
        this.loadData(scheduleBuilderDetailsStore.getAppliedFilters());
    }

    loadData = flow(function* (appliedFilters, update) {
        this.state = States.pending;

        this.resetDisplayData();

        this.schedules = [];

        // Reset the calendar events to be displaying
        this.displayedEvents = [];

        // Show reserve times on calendar
        let reservedTimeFiltersAsEvents = reservedTimeFilterStore.getReservedTimeFiltersAsEvents(scheduleBuilderStore.getInitialDate());
        if (reservedTimeFiltersAsEvents) {
            this.displayedEvents.push(reservedTimeFiltersAsEvents);
        }

        // Show registered sections on calendar
        let academicRecordAsEvents = scheduleBuilderPlanStore.getAcademicRecordAsEvents(scheduleBuilderStore.getInitialDate());
        if (academicRecordAsEvents) {
            this.displayedEvents.push(academicRecordAsEvents);
        }

        // Extract all course and activity codes planned in an array
        let pd = scheduleBuilderPlanStore.getPlanData();
        if(pd) {
            pd.map((item, index) => {
                let courseCd = normalize(item.courseDetails.code);
                this.planData.push(courseCd);
                if (item.planActivities.length > 0) {
                    item.planActivities.map((activity, aIdx) => {
                        this.planData.push(activity.activityId);
                    });
                }
            });
        }


        // Based on plan item selections, fetch a list of course inclusion filters
        let courseInclusionFilter = scheduleBuilderPlanStore.getCourseInclusionFilter();

        // analytics data
        let analyticsEventKey = update ?  "SB-section-filters-after-update" :  "SB-section-filters-before-update";
        let analyticsData = {
            noOfSectionFiltersUsed: scheduleBuilderDetailsStore.getSelectedFilters().length,
            sectionFiltersUsed: scheduleBuilderDetailsStore.getSelectedFilters().join('|'),
            dayTimeExclusions:  this.getReservedTimesAnalyticsData().join('|')
        };

        if (courseInclusionFilter.length > 0) {
            let postData = {
                termId: this.termId,
                appliedFilters: appliedFilters,
                courseInclusionFilter: courseInclusionFilter,
                reservedTimeFilter: reservedTimeFilterStore.getReservedTimeFilters(),
                scheduleAcademicRecords: scheduleBuilderPlanStore.getAcademicRecordForScheduleOptions()
            };

            try {
                this.schedules = yield api.searchForSchedules(postData);

                if (appStore.isDevMode()) {
                    console.log(`ScheduleBuilderOptionsStore: Data load complete for ${this.termId}`);
                }

                events.emit(ScheduleBuilderEvents.scheduleOptionsLoaded);

                analyticsData.noOfSchedules = this.schedules !== null && this.schedules.hasOwnProperty("possibleSchedules") ? this.schedules.possibleSchedules.length : 0;
                if (this.schedules.hasOwnProperty("possibleSchedules") && this.schedules.possibleSchedules.length === 0) {
                    analyticsData.errorCode = this.getNoPossibleScheduleCode().join('|');
                }

                //let analyticsDataString = Object.keys(analyticsData).map(key => key + '=' + analyticsData[key]).join(', ')
                this.analyticsStore.addEvent(analyticsEventKey,{}, analyticsData);

                this.state = States.done;
            } catch(error) {
                this.exceptionUtils.handleException(error, "ScheduleBuilderOptionsStore.loadData");
                this.state = States.error;
            }
        } else {
            this.schedules = {
                possibleSchedules: [],
                noPossibleScheduleErrorMsg: [
                    `NO_COURSES|No schedule options can be built when there are no courses in your plan. Add courses or <a href="/course">find courses</a> first, and then return to Schedule Builder to view schedule options.`
                ]
            };

            events.emit(ScheduleBuilderEvents.scheduleOptionsLoaded);

            analyticsData.noOfSchedules = 0;
            analyticsData.errorCode = 'NO_COURSES';
            //let analyticsDataString = Object.keys(analyticsData).map(key => key + '=' + analyticsData[key]).join(', ');
            this.analyticsStore.addEvent(analyticsEventKey, {}, analyticsData);

            this.state = States.done;
        }
    }.bind(this));

    getDisplayLimit() {
        return this.displayedEventsLimit;
    }

    getCssClass(isPinned) {
        if (isPinned) {
            return `scheduleBuilder-option--pinned`;
        } else {
            return this.scheduleDisplayClasses[0];
        }
    }

    toggleDisplayedSchedule(show, schedule) {
        this.state = States.pending;
        let scheduleIndex = this.displayedEvents.findIndex(displayedSchedule => displayedSchedule.activityHash === schedule.activityHash);
        if (show) {
            if (!schedule.isPinned) {
                this.scheduleDisplayClasses.splice(0, 1);
            }
            if (scheduleIndex === -1) {
                this.displayedEvents.push(schedule);
                this.displayedEventsLimit--;
            }
        } else {
            if (scheduleIndex !== -1) {
                let cssClass = this.displayedEvents[scheduleIndex].classNames[0];
                if (cssClass.indexOf('pinned') === -1) {
                    this.scheduleDisplayClasses.splice(0, 0, cssClass);
                }
                this.displayedEventsLimit++;
                this.displayedEvents.splice(scheduleIndex, 1);
            }
        }

        let eventObj = {
            activityHash: schedule.activityHash,
            shown: show
        }
        events.emit(ScheduleBuilderEvents.scheduleDisplayToggled, eventObj);
        this.state = States.done;
    }

    isScheduleShown(activityHash) {
        let scheduleIndex = this.displayedEvents.findIndex(displayedSchedule => displayedSchedule.activityHash === activityHash);
        return (scheduleIndex >= 0);
    }

    hasData() {
        return this.termId !== null;
    }

    getNoPossibleScheduleMsg() {
        if (this.schedules.hasOwnProperty("noPossibleScheduleErrorMsg") && this.schedules.noPossibleScheduleErrorMsg.length > 0) {
            let errorMessages = this.schedules.noPossibleScheduleErrorMsg.map(error => {
                let msg = error.split("|")[1];
                if (msg !== null && msg !== '') {
                    return msg;
                } else {
                    return null;
                }
            }).filter(error => error);

            if (errorMessages.length > 0) {
                return errorMessages;
            }
        }

        return null;
    }

    getNoPossibleScheduleCode() {
        if (this.schedules.hasOwnProperty("noPossibleScheduleErrorMsg") && this.schedules.noPossibleScheduleErrorMsg.length > 0) {
            let errorCodes = this.schedules.noPossibleScheduleErrorMsg.map(error => {
                let code = error.split("|")[0];
                if (code !== null && code !== '') {
                    return code;
                } else {
                    return null;
                }
            }).filter(error => error);

            if (errorCodes.length > 0) {
                return errorCodes;
            }
        }

        return null;
    }

    getReservedTimesAnalyticsData() {
        let reserveTimeFiltersListItems = reservedTimeFilterStore.getReservedTimeFilters().map(filter => {
            let filterDays = DaysOfWeek.filter(day => filter[day.key]).map(day => {
                return day.shortCode;
            });

            return (
                `${filterDays.join("")} from ${filter.startTime} - ${filter.endTime}`
        );
        });

        return reserveTimeFiltersListItems;
    }

    closeDisplayLimitMessage() {
        this.showDisplayLimitMessage = false;
    }

    openDisplayLimitMessage() {
        this.showDisplayLimitMessage = true;
    }

    hasAcademicRecordItems() {
        return scheduleBuilderPlanStore.hasAcademicRecordItems();
    }

    hasAcademicRecordWeekends() {
        return scheduleBuilderPlanStore.hasAcademicRecordWeekends();
    }

    showRegisteredScheduleCalendar() {
        return scheduleBuilderPlanStore.getPlanDataCount() === 0 && this.hasAcademicRecordItems();
    }
}

export default new ScheduleBuilderOptionsStore();